import styled from 'styled-components';
import {
  spacer16,
  spacer24,
  spacer64,
  spacer48,
  spacer8,
  spacer6,
  spacer12,
} from 'styles/spacers';
import { maxWidth, minWidth } from 'styles/media-queries';
import { black, white } from 'styles/colors';
import { fontWeights, navHeight } from 'styles/variables';
import { Icon } from 'components/atoms/icon';
import { red25 } from '@spotify-internal/encore-web/core';
import { scrollHorizontalInvisible } from 'styles/utils';
import { offset } from 'styles/grid';
import { levels } from 'styles/z-index';

export const ErrorBanner = styled.div`
  background-color: ${red25};
  color: ${white};
  padding: ${spacer16} var(--_gutter);
  display: flex;
  height: fit-content;
  align-items: center;
  gap: ${spacer16};
  position: absolute;
  top: ${navHeight.smToLg}rem;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${levels.highlight};
`;

export const ErrorIcon = styled(Icon)`
  width: 2.2rem;
  height: 2.2rem;
  line-height: 2.2rem;

  svg,
  div {
    height: inherit;
    width: inherit;
  }
  path {
    fill: ${white};
  }
`;

export const ErrorMessage = styled.p`
  font-size: 1.4rem;
  font-weight: ${fontWeights.normal};
  line-height: 1.6rem;
`;

export const MessageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer8};
`;

export const MessageFieldLabel = styled.div`
  font-size: 1.2rem;
  color: ${white};
  line-height: 160%;
`;

export const GenerateButton = styled.button<{
  disabled?: boolean;
}>`
  grid-column: content;

  width: var(--_submit_button-width);
  padding: ${spacer12} ${spacer24};
  background: ${white};
  color: ${black};
  border: none;
  border-radius: ${spacer6};
  font-size: 1.6rem;
  font-weight: ${fontWeights.extra_bold};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:disabled {
    cursor: not-allowed;
  }
`;

export const GridWrapper = styled.div`

   /**
   * CSS Variables:
   * --_* : Internal layout vars used in this file only
   * --audio-card-* : Public vars for child components
   */
  --_gutter: ${spacer24};
  --carousel-gutter: var(--_gutter);
  --audio_card-width: 144px;
  --audio_card-gap: ${spacer12};
  --_audio_card-count: 4; // unfortunately :(

  --_submit_button-width: 100%;

  --_row-gap: ${spacer16};
  --_column-width: 1fr;
  --_column-width_max: calc(
    var(--audio_card-width) * var(--_audio_card-count) +
      var(--audio_card-gap) * (var(--_audio_card-count) - 1)
  );
  --_column-gap: ${spacer64};

  --_grid-width_max: calc(
    var(--_column-width_max) + var(--_gutter) * 2
  );

  ${minWidth.sm`
    --_gutter: ${offset.sm}px;
  `}

  ${minWidth.md`
    --_gutter: ${offset.md}px;
  `}

  ${minWidth.lg`
    --carousel-gutter: 0;
    --_gutter: ${offset.lg}px;
    --_submit_button-width: min-content;
    --_grid-width_max: calc(
      var(--_column-width_max) * 2 + var(--_gutter) * 2 + var(--_column-gap)
    );
  `}

  ${minWidth.xl`
    --_gutter: ${offset.xl}px;
  `}
`;

export const ContentGrid = styled.article`
  position: relative;
  display: grid;
  row-gap: var(--_row-gap);

  width: 100%;
  max-width: var(--_grid-width_max);
  margin: auto;

  /* Single column layout: mobile and tablet */
  grid-template-columns:
    [full-start] var(--_gutter)
    [content-start] var(--_column-width)
    [content-end] var(--_gutter) [full-end];

  /* Two column layout: desktop */
  ${minWidth.lg`
    grid-template-columns:
      [full-start] var(--_gutter)
      [content-start] var(--_column-width) [content-end]
      var(--_column-gap)
      [preview-start] var(--_column-width) [preview-end]
      var(--_gutter) [full-end];
  `}
`;

export const ContentGridFormArea = styled.section`
  grid-column: content;

  min-width: 0; /* Prevent overflow without scroll: allow flex items to shrink below their min-content width */
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--_row-gap);
`;

export const ContentGridCarouselArea = styled.section`
  grid-column: full;

  ${minWidth.lg`
    grid-column: content;
  `}

  display: grid;
  grid-template-columns: subgrid;
  gap: var(--_row-gap);

  min-width: 0;

  ${scrollHorizontalInvisible}
`;

export const ContentGridPreviewArea = styled.section`
  grid-column: content;

  ${minWidth.lg`
    grid-column: preview;
    grid-row: 1 / span 2;
  `}
`;

export const ContentGridSubmitArea = styled.section`
  grid-column: full;

  display: grid;
  grid-template-columns: subgrid;

  // Sticky button with gradient on mobile and tablet
  ${maxWidth.md`
    position: sticky;
    bottom: 0;
    padding: ${spacer48} 0 ${spacer16};
    margin-top: calc(${spacer48} * -0.5); // calc based on top padding
    background: linear-gradient(180deg, transparent, ${black} 33%);
  `}
`;

export const LegalDisclaimer = styled.p`
  grid-column: content;

  font-size: 1.2rem;
  line-height: 160%;
  color: #7c7c7c;

  ${minWidth.lg`
    grid-column-start: content;
    grid-column-end: preview;
  `}
`;
